import React, { useMemo } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DashboardMetricItem } from '../../api/dashboard';
import { DashboardMetricSection, MetricUpdateButton } from './';
import MetricCounter from './MetricCounter';
import { TALK_ROOM_THEME } from '../../types/talk';

interface TalkRoomStatusSectionProps {
  metricData?: DashboardMetricItem;
  isLoading: boolean;
  onUpdate?: (metricKey: string) => Promise<void>;
}

/**
 * 対話ルームステータスを表示するセクション
 */
const TalkRoomStatusSection: React.FC<TalkRoomStatusSectionProps> = ({ metricData, isLoading, onUpdate }) => {
  const navigate = useNavigate();
  // 申込中の対話ルーム一覧に遷移する関数
  const handleNavigateToApplyingTalkRooms = () => {
    navigate('/talk_rooms', { state: { selectedStatuses: [ 'APPLYING' ] } });
  };
  // 準備中の対話ルーム一覧に遷移する関数
  const handleNavigateToPreparingTalkRooms = () => {
    navigate('/talk_rooms', { state: { selectedStatuses: [ 'PREPARING' ] } });
  };
  // フィードバック待ちの対話ルーム一覧に遷移する関数
  const handleNavigateToWaitFeedbackTalkRooms = () => {
    navigate('/talk_rooms', { state: { selectedStatuses: [ 'WAIT_FEEDBACK' ] } });
  };
  const talkCounts = useMemo(() => {
    if (!metricData) {
      return {
        currentMonth: 0,
        previousMonth: 0
      };
    }
    const {
      talk_room__talk_count_this_month = 0,
      talk_room__talk_count_last_month = 0
    } = metricData.current_values;
    return {
      currentMonth: Number(talk_room__talk_count_this_month),
      previousMonth: Number(talk_room__talk_count_last_month)
    };
  }, [ metricData ]);
  // メトリクスデータからセクションコンテンツを生成する関数
  const renderTalkRoomStatusContent = (data: DashboardMetricItem) => {
    const {
      talk_room__applying_count = 0,
      talk_room__preparing_count = 0,
      talk_room__wait_feedback_count = 0
    } = data.current_values;
    return (
      <Grid
        container
        spacing={2}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: { xs: 'center', sm: 'flex-start' }
        }}
      >
        {/* 基本セクション - ステータス指標 */}
        <Grid item xs={12} container spacing={2} mb={2}>
          {/* 申込中の対話ルーム数 */}
          <Grid item xs={6} sm={3} md={3} lg={2} xl={2}>
            <MetricCounter
              label="申込中"
              value={talk_room__applying_count}
              color="warning.light"
              tooltip="ステータス: 申込中(APPLYING)"
              onClick={handleNavigateToApplyingTalkRooms}
            />
          </Grid>

          {/* 準備中の対話ルーム数 */}
          <Grid item xs={6} sm={3} md={3} lg={2} xl={2}>
            <MetricCounter
              label="準備中"
              value={talk_room__preparing_count}
              color="warning.dark"
              tooltip="ステータス: 準備中(PREPARING)"
              onClick={handleNavigateToPreparingTalkRooms}
            />
          </Grid>

          {/* フィードバック待ちの対話ルーム数 */}
          <Grid item xs={6} sm={3} md={3} lg={2} xl={2}>
            <MetricCounter
              label="フィードバック待ち"
              value={talk_room__wait_feedback_count}
              color="info.dark"
              tooltip="ステータス: フィードバック待ち(WAIT_FEEDBACK)"
              onClick={handleNavigateToWaitFeedbackTalkRooms}
            />
          </Grid>

          {/* 今月と先月の対話数 */}
          <Grid item xs={6} sm={3} md={3} lg={2} xl={2}>
            {(() => {
              const monthlyDiff = talkCounts.currentMonth - talkCounts.previousMonth;
              let monthPercentChange: number | null = 0;
              if (talkCounts.previousMonth > 0) {
                monthPercentChange = Math.round((monthlyDiff / talkCounts.previousMonth) * 100);
                // 極端なパーセンテージ（999%以上）は表示しない
                if (monthPercentChange > 999) {
                  monthPercentChange = null;
                }
              } else if (monthlyDiff > 0) {
                // 先月が0の場合は「新規」としてパーセント表示しない
                monthPercentChange = null;
              }
              return (
                <Box sx={{ position: 'relative', height: '100%' }}>
                  <MetricCounter
                    label="今月の対話数"
                    value={talkCounts.currentMonth}
                    tooltip={
                      monthlyDiff !== 0
                        ? `先月比: ${monthlyDiff > 0 ? '+' : ''}${monthlyDiff} 件 ${
                          monthPercentChange === null
                            ? talkCounts.previousMonth === 0 ? '(先月0件)' : ''
                            : `(${monthPercentChange}%)`
                        }`
                        : '先月比: 変化なし'
                    }
                    disableHoverEffect={true}
                  />
                  <Typography
                    color="text.secondary"
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8
                    }}
                  >
                    先月: <span style={{ fontSize: '1.2rem' }}>{talkCounts.previousMonth}</span>
                  </Typography>
                </Box>
              );
            })()}
          </Grid>
        </Grid>
      </Grid>
    );
  };
  return (
    <DashboardMetricSection
      title="対話"
      description=""
      metricData={metricData}
      isLoading={isLoading}
      renderContent={renderTalkRoomStatusContent}
      themeColor={TALK_ROOM_THEME.DARK} // 対話テーマカラー #1976d2
      renderButton={
        onUpdate && (
          <MetricUpdateButton
            metricKey="talk_room_status"
            onUpdate={onUpdate}
            disabled={isLoading}
          />
        )
      }
    />
  );
};
export default TalkRoomStatusSection;
